import {action, makeObservable, observable} from "mobx";
import i18n from "i18next";
import {IPersonTypesDomainStore} from "../../domainStore/pages/personTypesDomainStore";

/**
 * The resolved display value for a calendar's "Resident Groups" column.
 * `isLegacy` is true only when the value came from the single legacy
 * `personType` id and a successful CMS lookup confirmed no mapping exists
 * (empty `personTypeIds`). A missing `personTypeIds` (null/undefined) means
 * CMS enrichment was unavailable, which is not flagged as migration state.
 */
export interface IResidentGroupsDisplay {
  text: string;
  isLegacy: boolean;
}

/**
 * UI state for the Village Calendars admin page.
 */
export interface IVillageCalendarsUiStore {
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  errorMessage: string;
  setLoading: (value: boolean) => void;
  setSaving: (value: boolean) => void;
  setDeleting: (value: boolean) => void;
  setErrorMessage: (message: string) => void;
  /**
   * Resolve a list of person type ids to a comma-separated string of
   * person type names via the person types domain store. Unknown ids fall
   * back to the raw id so nothing is silently dropped; an empty/undefined
   * input returns "-".
   */
  getPersonTypeNames: (ids?: string[] | null) => string;
  /**
   * Resolve the "Resident Groups" column value for a calendar, preferring
   * the CMS-backed `personTypeIds`. Falls back to the legacy single
   * `personType` id (resolved to a name, or "Other" for calendar-specific
   * ids that aren't Independent/Serviced) when no CMS mapping exists yet.
   */
  getResidentGroupsDisplay: (
    personTypeIds?: string[] | null,
    legacyPersonType?: string | number | null
  ) => IResidentGroupsDisplay;
}

export class VillageCalendarsUiStore implements IVillageCalendarsUiStore {
  private readonly personTypesDomainStore: IPersonTypesDomainStore;
  isLoading = false;
  isSaving = false;
  isDeleting = false;
  errorMessage = "";

  constructor(personTypesDomainStore: IPersonTypesDomainStore) {
    makeObservable(this, {
      isLoading: observable,
      isSaving: observable,
      isDeleting: observable,
      errorMessage: observable,
      setLoading: action,
      setSaving: action,
      setDeleting: action,
      setErrorMessage: action
    });
    this.personTypesDomainStore = personTypesDomainStore;
  }

  setLoading = (value: boolean) => {
    this.isLoading = value;
  };

  setSaving = (value: boolean) => {
    this.isSaving = value;
  };

  setDeleting = (value: boolean) => {
    this.isDeleting = value;
  };

  setErrorMessage = (message: string) => {
    this.errorMessage = message;
  };

  getPersonTypeNames = (ids?: string[] | null): string => {
    if (!ids || ids.length === 0) return "-";
    return ids
      .map((id) => this.personTypesDomainStore.getById(id)?.name ?? id)
      .join(", ");
  };

  getResidentGroupsDisplay = (
    personTypeIds?: string[] | null,
    legacyPersonType?: string | number | null
  ): IResidentGroupsDisplay => {
    if (personTypeIds && personTypeIds.length > 0) {
      return {text: this.getPersonTypeNames(personTypeIds), isLegacy: false};
    }

    // The integration API serialises an unset legacy person type as 0.
    if (
      legacyPersonType === null ||
      legacyPersonType === undefined ||
      String(legacyPersonType) === "" ||
      String(legacyPersonType) === "0"
    ) {
      return {text: "-", isLegacy: false};
    }

    const legacyName = this.personTypesDomainStore.getById(
      String(legacyPersonType)
    )?.name;
    // Empty array = CMS lookup succeeded and confirmed no mapping (legacy);
    // null/undefined = enrichment unavailable, so don't flag an outage as
    // migration state.
    const isConfirmedUnmapped =
      Array.isArray(personTypeIds) && personTypeIds.length === 0;
    return {
      text: legacyName ?? i18n.t("villageCalendars:labels.otherPersonType"),
      isLegacy: isConfirmedUnmapped
    };
  };
}
